import { InvoiceStatusEnum } from "../enums/invoice/invoice.enum";
import { PaymentStatusTypes } from "../enums/payment/payment.enum";
import { IPayment } from "../interfaces/payment/payment.interface";
import {ProjulRegex} from "./regex";
import * as _ from 'lodash';


export class NumberUtil {

  private static _defaultPrecision: number = 2;

  public static CalculatePercentAmount(amount: number, percent: number, round?: boolean, precision?: number) {
    let result = (((amount || 0) * (percent || 0)) / 100) || 0;
    return this.roundNumber(result, round, precision);
  }

  public static CalculateTotalPercent(secondAmt: number, firstAmt: number, round: boolean = true, precision?: number) {
    let result = (((firstAmt || 0) / (secondAmt || 1)) * 100) || 0;
    return this.roundNumber(result, round, precision);
  }

  public static Multiply(firstAmt: number, secondAmt: number, round?: boolean, precision?: number) {
    let result = (firstAmt || 0) * (secondAmt || 0);
    return this.roundNumber(result, round, precision);
  }

  public static Subtract(x: number, y: number, keepPositive: boolean = true, round?: boolean, precision?: number): number {
    let result = (x || 0) - (y || 0);
    if (keepPositive)
      result = result > 0 ? result : 0;

    return this.roundNumber(result, round, precision);
  }

  public static ToNumber(value: any, digits: number = 2): number {
    let removeFormatting = this.RemoveFormatting(value, true);
    return decimal2(+(removeFormatting || 0), undefined, digits);
  }

  public static RemoveFormatting(value: string, allowNegative: boolean): string {
    let result = value && value.toString().replace(allowNegative ? ProjulRegex.NumbersDotAndNegative : ProjulRegex.NumbersAndDot, '').replace(/\B(?=(\d{3})+ (?!\d))/g, ',');
    if (result) {
      if (result?.indexOf('-') > -1) {
        result = (result as any)?.replaceAll("-", '');
        result = "-" + result;
      }
    }
    return result || '';
  }

  public static roundNumber(value: number, round?: boolean, precision?: number) {
    return round ? _.round(value, precision || this._defaultPrecision) : value;
  }
}

export function parseJson(value: any) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

export function decimal2(value: number, floor?: any, digits = 2) {
  if (!value || isNaN(value)) {
    return value;
  }
  let charge = value < 0 ? -1 : 1;
  if (floor) {
    return (charge * Math.floor(Math.abs(value) * 10 ** digits)) / 10 ** digits;
  }
  return (charge * Math.round(Math.abs(value) * 10 ** digits)) / 10 ** digits;
}

export function objectIsEmpty(obj: any) {
  return Object.values(obj || {}).every((v) => !v);
}

export function concatFilePath(baseUri: any, fileName: any) {
  return (baseUri || '') + encodeURIComponent(fileName);
}

export function isImageFileExtension(extension: string): boolean {
  const ext = (extension || '').replace(/^\./, '').toLowerCase();
  return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'tiff'].includes(ext);
}

/**
 * Relative path to the backend's `web/{base}-512{ext}` variant, to combine with UriPath via
 * concatFilePath. A list must never render originals: they decode the browser out of memory.
 */
export function getThumbnailPath(photo: any): string {
  if (!photo) {
    return '';
  }
  const fileName: string = photo.FileName || '';
  const dot = fileName.lastIndexOf('.');
  const base = dot > 0 ? fileName.substring(0, dot) : fileName;
  const ext = photo.FileExtension || (dot > 0 ? fileName.substring(dot) : '');
  // Non-image files (PDF, docs) have no thumbnail variant.
  if (!isImageFileExtension(ext)) {
    return base + ext;
  }
  const height = Number(photo.Height || photo.ImageHeight || 0);
  if (height > 0 && height < 128) {
    // Original is already smaller than the thumbnail — use it directly.
    return base + ext;
  }
  // height >= 128, or unknown (0) — always use the 512px thumbnail variant.
  return 'web/' + base + '-512' + ext;
}

/**
 * The one photo URL builder for every document render: thumbnail by default, original when
 * `failed`. Wire `failed` to the <img> (error) handler so a missing thumbnail still resolves.
 */
export function getPhotoDisplayUrl(photo: any, failed?: boolean): string {
  if (!photo) {
    return '';
  }
  const path = failed ? (photo.FileName || '') : getThumbnailPath(photo);
  // Already-absolute URLs (blob:, data:, http) must be used verbatim, never prefixed.
  if (/^(blob:|data:|https?:)/i.test(path)) {
    return path;
  }
  return concatFilePath(photo.UriPath, path);
}

/** Marks a photo's thumbnail as failed so the view falls back to the full-res original. */
export function markThumbnailFailed(photo: any): void {
  if (photo && !photo._thumbFailed) {
    photo._thumbFailed = true;
  }
}

export async function downloadFileFromUrl(fileUrl: string, fileName: string) {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const urlCreator = window.URL || window.webkitURL;
    const imageUrl = urlCreator.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(imageUrl);
  } catch (error) {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

/** The one rule for "Company (First Last)": the flag alone never counts, the company must be non-blank. */
export function usesCompanyAsDisplayName(displayCompanyName: unknown, companyName: unknown): boolean {
  return !!displayCompanyName && typeof companyName === 'string' && companyName.trim().length > 0;
}

export function getCustomerNameWithCompanyForEstInv(item: any) {
  if (!item) {
    return '';
  }
  let returnString = '';
  const companyName = (item?.CustomerCompanyName || item?.Customer?.CompanyName || '').trim();
  let customer = {
    CompanyName: companyName,
    DisplayCompanyName: usesCompanyAsDisplayName(item?.DisplayAsCustomerCompanyName, companyName),
    FirstName: (item?.CustomerFirstName || item?.Customer?.FirstName || '').trim(),
    LastName: (item?.CustomerLastName || item?.Customer?.LastName || '').trim(),
  };
  if (customer) {
    if (customer?.DisplayCompanyName) {
      returnString = (customer?.CompanyName != null ? customer?.CompanyName : '') 
        + (customer?.CompanyName && (customer.FirstName || customer.LastName) ? ' ' : '') 
        + (customer.FirstName || customer.LastName ? `(${(customer?.FirstName ?? '') + (customer?.LastName ? customer?.FirstName ? ' ' : '' : '') + (customer?.LastName ?? '')})` : '');
    }
    else {
      returnString = (customer?.FirstName ?? '') 
        + (customer?.LastName && customer?.FirstName ? ' ' : '') 
        + (customer?.LastName ?? '') 
        + (customer?.CompanyName && (customer.FirstName || customer.LastName) ? ' ' : '') 
        + (customer?.CompanyName ? `(${customer?.CompanyName})` : '');
    }
  }
  return returnString;

}
export function validInvoicePaymentsExists(payments:IPayment[])
{
  return (payments || []).some(x => x.Status == PaymentStatusTypes.Completed || x.Status == PaymentStatusTypes.PartiallyRefunded || x.Status == PaymentStatusTypes.FullyRefunded);
}

export function IsInvoicePublished(status: InvoiceStatusEnum) {
  return (status === InvoiceStatusEnum.Paid || status === InvoiceStatusEnum.PendingPayment || status === InvoiceStatusEnum.Partial || status === InvoiceStatusEnum.Overdue);
}

export function GetInvoicePayments(payments: IPayment[]) {
  return (payments || []).filter(x => x.Status == PaymentStatusTypes.Completed || x.Status == PaymentStatusTypes.PartiallyRefunded || x.Status == PaymentStatusTypes.FullyRefunded);
}

export function ValidInvoicePaymentsExists(payments: IPayment[]) {
  return (payments || []).some(x => x.Status == PaymentStatusTypes.Completed || x.Status == PaymentStatusTypes.PartiallyRefunded || x.Status == PaymentStatusTypes.FullyRefunded);
}

export function getCustomerNameWithCompany(item: any, defaultToEmail?: boolean) { //This is a global function which provides us the customer name by checking all rules. If you change in this method this will affect all over the application.
  if (!item) {
    return '';
  }
  let returnString = '';
  const companyName = (item?.CompanyName || item?.companyName || '').trim();
  let customer = {
    CompanyName: companyName,
    DisplayCompanyName: usesCompanyAsDisplayName(item?.DisplayCompanyName || item?.displayCompanyName, companyName),
    FirstName: (item?.FirstName || item?.firstName || '').trim(),
    LastName: (item?.LastName || item?.lastName || '').trim(),
    Emails: item?.Emails || item?.emails,
  };
  if (customer) {
    if (customer?.DisplayCompanyName) {
      returnString = (customer?.CompanyName != null ? customer?.CompanyName : '')
        + (customer?.CompanyName && (customer.FirstName || customer.LastName) ? ' ' : '')
        + (customer.FirstName || customer.LastName ? `(${(customer?.FirstName ?? '') + (customer?.LastName ? customer?.FirstName ? ' ' : '' : '') + (customer?.LastName ?? '')})` : '');
    }
    else {
      returnString = (customer?.FirstName ?? '')
        + (customer?.LastName && customer?.FirstName ? ' ' : '')
        + (customer?.LastName ?? '')
        + (customer?.CompanyName && (customer.FirstName || customer.LastName) ? ' ' : '')
        + (customer?.CompanyName ? `(${customer?.CompanyName})` : '');
    }

    if (defaultToEmail && !!customer?.Emails?.[0]?.Email && !returnString) {
      returnString = customer.Emails[0].Email;
    }
  }
  return returnString;

}